import type { ApiKey, ApiKeyCreated, CreateApiKeyInput, Page } from '@servicetim/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { adminPath, useApiContext } from './client';
import { DEFAULT_PAGE_SIZE, pageQuery, usePagedList } from './paging';

const base = adminPath('/api-keys');

/** `GET /admin/v1/api-keys?includeRevoked=` */
export function useApiKeys(options: { includeRevoked?: boolean; pageSize?: number } = {}) {
  const { request, keys } = useApiContext();
  const pageSize = options.pageSize ?? DEFAULT_PAGE_SIZE;
  const includeRevoked = options.includeRevoked ?? false;
  return usePagedList<ApiKey>({
    queryKey: keys.apiKeys.list({ includeRevoked, pageSize }),
    fetchPage: (cursor, signal) =>
      request<Page<ApiKey>>('GET', base, {
        query: { includeRevoked, ...pageQuery(cursor, pageSize) },
        signal,
      }),
  });
}

export function useApiKey(apiKeyId: string | undefined) {
  const { request, keys } = useApiContext();
  return useQuery({
    queryKey: keys.apiKeys.detail(apiKeyId ?? ''),
    queryFn: ({ signal }) => request<ApiKey>('GET', `${base}/${apiKeyId}`, { signal }),
    enabled: apiKeyId !== undefined,
  });
}

/** `POST /admin/v1/api-keys`: the response carries `secret` exactly once. */
export function useCreateApiKey() {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: (input: CreateApiKeyInput) => request<ApiKeyCreated>('POST', base, { body: input }),
    onSuccess: () => invalidate(keys.apiKeys.all),
  });
}

/** `DELETE /admin/v1/api-keys/{id}` (revoke). */
export function useRevokeApiKey() {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: (id: string) => request<unknown>('DELETE', `${base}/${id}`),
    onSuccess: () => invalidate(keys.apiKeys.all),
  });
}
